import type { FC, PropsWithChildren } from 'react';
import { useContext, useMemo, useState } from 'react';

import type {
  InFieldSpecificUserPreference,
  UserPreferenceLocation,
} from '@cognite/apm-client';
import type { Discipline } from '@cognite/apm-config';
import { Heading, Loader } from '@cognite/cogs-core-v11';
import { useTranslation } from 'react-i18next';

import type { LocationOption } from '../../features/app-config/hooks/types';
import type { DisciplineOption } from '../../features/app-config/hooks/use-configured-disciplines';
import { PageContainerStyle } from '../../pages/elements';

import { CdmUserOnboardingProviderContext } from './cdm-user-onboarding-provider.context';
import { getDisciplineOptionsForLocation } from './utils/getDisciplineOptionsForLocation';

export const CdmUserOnboardingProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const {
    useSelectedLocationContext,
    useInfieldLanguage,
    useUpsertCurrentUserPreferences,
    useCurrentUserContext,
    AppConfigErrorScreen,
    UserOnboarding,
  } = useContext(CdmUserOnboardingProviderContext);
  const { user } = useCurrentUserContext();
  const userPreferences = user?.preferences?.infield;
  const { mutateAsync: upsertUserPreferences } =
    useUpsertCurrentUserPreferences();

  const { t } = useTranslation('user');
  const {
    locationOptions,
    selectedLocationConfig,
    allLocationConfigs,
    isLoading,
  } = useSelectedLocationContext();

  const { selectedLanguage, supportedLanguages, updateLanguage } =
    useInfieldLanguage();

  const [localUserPreferences, setLocalUserPreferences] =
    useState<InFieldSpecificUserPreference>(userPreferences);

  const disciplineOptions = useMemo(
    () =>
      getDisciplineOptionsForLocation(
        allLocationConfigs,
        localUserPreferences?.location
      ),
    [allLocationConfigs, localUserPreferences?.location]
  );

  const handleSelectOnboardingItem = (
    preference: keyof NonNullable<typeof localUserPreferences>,
    value: Discipline | UserPreferenceLocation
  ) => {
    setLocalUserPreferences({ ...localUserPreferences, [preference]: value });
  };

  const handleSaveUserOnboarding = async () => {
    await upsertUserPreferences({
      preference: 'infield',
      data: localUserPreferences,
    });
  };

  const getLocationOption = (
    location?: UserPreferenceLocation
  ): LocationOption | undefined => {
    if (!location) return;
    return locationOptions.find(
      (opt) =>
        opt.value.externalId === location.externalId &&
        opt.value.space === location.space
    );
  };

  const getDisciplineOption = (
    discipline?: Discipline
  ): DisciplineOption | undefined => {
    if (!discipline) return;
    return disciplineOptions.find(
      (opt) => opt.value.externalId === discipline.externalId
    );
  };

  if (isLoading) {
    return (
      <Loader
        infoText={
          <Heading level={6}>
            {t('loadingUserData', 'Loading user data')}
          </Heading>
        }
      />
    );
  }

  if (!locationOptions || locationOptions.length === 0) {
    return (
      <AppConfigErrorScreen
        title={t(
          'USER_ERROR_LOCATION_MISSING_TITLE',
          'Error: Location is missing.'
        )}
        body={t(
          'USER_ERROR_LOCATION_MISSING_DESCRIPTION',
          "The InField admin has not defined any location yet, or you don't have access to any of the configured locations. Contact your admin if you have any questions."
        )}
      />
    );
  }

  if (!selectedLocationConfig) {
    return (
      <PageContainerStyle>
        <UserOnboarding
          languages={supportedLanguages}
          selectedLanguage={selectedLanguage}
          onSelectLanguage={updateLanguage}
          locations={locationOptions}
          selectedLocation={getLocationOption(localUserPreferences?.location)}
          onSelectLocation={(location) =>
            handleSelectOnboardingItem('location', location)
          }
          disciplines={disciplineOptions}
          selectedDiscipline={getDisciplineOption(
            localUserPreferences?.discipline
          )}
          onSelectDiscipline={(discipline) =>
            handleSelectOnboardingItem('discipline', discipline)
          }
          onComplete={handleSaveUserOnboarding}
        />
      </PageContainerStyle>
    );
  }

  return <>{children}</>;
};
